import { styled } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';

export const Page = styled('main')(({ theme }) => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  padding: 40,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  color: theme.palette.novotalk.$textColorDarkBlue,
  backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const Title = styled('h1')(({ theme }) => ({
  margin: '0 0 0 14px',
  color: theme.palette.novotalk.$novotalkBlue,
  fontSize: 24,
  fontWeight: 500,
  textTransform: 'uppercase',
}));

export const TableFrame = styled('section')(({ theme }) => ({
  minHeight: 0,
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  borderRadius: 6,
  backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const AttentionFlag = styled('span')({
  width: 22,
  height: 22,
  borderRadius: '50%',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d33c5666',
  color: '#d33c56',
  backgroundColor: '#d33c561f',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
});

export const UserCell = styled('span')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
});

export const UserAvatar = styled('span')(({ theme }) => ({
  width: 30,
  height: 30,
  flex: '0 0 30px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.novotalk.$textColorReversed,
  backgroundColor: theme.palette.novotalk.$activeColor,
  fontSize: 11,
  fontWeight: 700,
}));

export const NameCell = styled('span')({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1.25,
  '& > span:first-of-type': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const SecondaryText = styled('span')(({ theme }) => ({
  marginTop: 2,
  color: theme.palette.novotalk.$textColorDisabled,
  fontSize: 12,
}));

export const PhoneExists = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 700,
});

export const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'success' | 'error' | 'neutral' }>(({ tone }) => {
  const tones = {
    success: { backgroundColor: '#e4f6ee', color: '#2f9d70' },
    error: { backgroundColor: '#fdeaee', color: '#d3567a' },
    neutral: { backgroundColor: '#eef1f5', color: '#5b6b82' },
  };
  return {
    ...tones[tone],
    maxWidth: '100%',
    padding: '3px 10px',
    borderRadius: 11,
    display: 'inline-flex',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 700,
  };
});

export const MentorCell = styled('span')({
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});

export const MentorAvatar = styled('span')({
  width: 28,
  height: 28,
  flex: '0 0 28px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#5b6b82',
  backgroundColor: '#eef1f5',
  fontSize: 10.5,
  fontWeight: 700,
});

export const MentorOverflow = styled('span')({
  minWidth: 24,
  height: 24,
  padding: '0 6px',
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#8493a6',
  backgroundColor: '#f5f7fa',
  fontSize: 10.5,
  fontWeight: 700,
});

export const DateCell = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  color: theme.palette.novotalk.$textColorDarkBlue,
  lineHeight: 1.25,
  fontSize: 13,
  '& small': {
    marginTop: 2,
    color: theme.palette.novotalk.$textColorDisabled,
    fontSize: 11.5,
  },
}));

export const QuickActionsRoot = styled('span')({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
});

export const ActionButton = styled('button')(({ theme }) => ({
  width: 30,
  height: 30,
  padding: 0,
  border: 0,
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.novotalk.$textColorDarkBlue,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  '&:hover, &:focus-visible': {
    backgroundColor: '#eef1f5',
  },
  '&:disabled': {
    cursor: 'wait',
  },
}));

export const ActionMenuItem = styled(MenuItem)(({ theme }) => ({
  width: 158,
  padding: '8px 12px',
  gap: 9,
  color: theme.palette.novotalk.$textColorDarkBlue,
  font: 'inherit',
  fontSize: 13,
}));
